import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Outlet,
  useLocation,
  useMatches,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { isValid, parse } from 'date-fns';
import { ReportNav } from '@data/components/report/ReportNav/ReportNav';
import { DataNotFoundPage, HeaderCard, type DateRange } from '@data/main';
import {
  ReportFilterBar,
  Drilldown,
} from '@data/components/report/ReportFilterBar/ReportFilterBar';
import { REPORTS, type ProductCode } from '@data/config/reports';
import { getSchoolYearDateRange } from '@data/utils/get-school-year';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Resolve the date range from the URL (`from`/`to` as yyyy-MM-dd) so a range
// carried over from a drill-down survives refreshes and shared links. Falls
// back to the current school year when the params are absent, unparseable, or
// inverted (`from` after `to`) — e.g. on the base overview after navigating
// back from a drill-down, where the params are dropped.
function resolveDateRange(from: string | null, to: string | null): DateRange {
  if (from && to) {
    const fromDate = parse(from, 'yyyy-MM-dd', new Date());
    const toDate = parse(to, 'yyyy-MM-dd', new Date());
    if (isValid(fromDate) && isValid(toDate) && fromDate <= toDate) {
      return { from: fromDate, to: toDate };
    }
  }
  return getSchoolYearDateRange();
}

function useDrilldown(): Drilldown {
  const { pathname } = useLocation();
  if (pathname.endsWith('/student')) return Drilldown.Student;
  if (pathname.endsWith('/class')) return Drilldown.Class;
  if (pathname.includes('/engagement')) return Drilldown.EngagementFeed;
  return Drilldown.AllClasses;
}

interface ReportOutletContext {
  dateRange: DateRange;
  setDateRange: (dateRange: DateRange) => void;
  drilldown: Drilldown;
  productId: ProductCode;
  reportPath: string | undefined;
}

export function useReportOutletContext() {
  return useOutletContext<ReportOutletContext>();
}

export function ReportLayout() {
  const { productId } = useParams<{ productId: string }>();
  const { dndShowRise } = useFlags();
  const { pathname } = useLocation();
  const matches = useMatches();
  const [searchParams] = useSearchParams();
  const drilldown = useDrilldown();

  const classIds = searchParams.getAll('classId');
  const studentIds = searchParams.getAll('studentId');
  const fromParam = searchParams.get('from');
  const toParam = searchParams.get('to');

  const [dateRange, setDateRange] = useState<DateRange>(() =>
    resolveDateRange(fromParam, toParam)
  );

  const pathSegments = matches[matches.length - 1]?.pathname.split('/') ?? [];
  const report = REPORTS.find((r) => pathSegments.includes(r.path));
  const reportLabel = report?.label;
  const reportPath = report?.path;

  const handleDateRangeChange = useCallback((range: DateRange) => {
    setDateRange(range);
  }, []);

  // Keep the date range in sync with the URL. A range carried on `from`/`to`
  // (e.g. from a drill-down or a report switch within the same product) is
  // applied; navigating to a URL without those params — back to the base
  // overview, switching reports, or landing on another product — resets to the
  // current school year. Skips the initial mount, already seeded above.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setDateRange(resolveDateRange(fromParam, toParam));
  }, [fromParam, toParam]);

  if (!productId) return null;

  if (productId === 'rise' && !dndShowRise) {
    return <DataNotFoundPage />;
  }

  return (
    <div className="min-h-[calc(100vh-80px)] min-w-0 flex-1 bg-brand-grey_7 antialiased">
      <div className="mx-auto flex w-full min-w-0 max-w-[1440px] flex-col py-6 mobile:px-4 tablet:px-10">
        <HeaderCard productId={productId} />
        <div className="flex w-full min-w-0 flex-col tablet:flex-row tablet:gap-6">
          <ReportNav />
          <div className="flex min-w-0 flex-1 flex-col rounded-lg bg-white p-8 mobile:mt-4 tablet:mt-0">
            {reportLabel && (
              <h2 className="mb-4 text-2xl font-bold text-black tablet:mb-8 tablet:text-3xl">
                {reportLabel}
              </h2>
            )}
            <ReportFilterBar
              key={pathname}
              drilldown={drilldown}
              classIds={classIds}
              studentIds={studentIds}
              edApp={productId as ProductCode}
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
            />
            <main className="min-w-0">
              <Outlet
                context={
                  {
                    dateRange,
                    setDateRange,
                    drilldown,
                    productId: (productId ?? '') as ProductCode,
                    reportPath,
                  } satisfies ReportOutletContext
                }
              />
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
